import { useMutation } from "react-query";
import { z } from "zod";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

type GetCampaignSsqPostVersionComparisonPayload = {
    campaignId: number;
    ssqVersionId: number;
    sourceSsqVersionId: number;
    page: number;
    itemsPerPage: number;
};

const SsqPostVersionComparisonSchema = z.object({
    postId: z.number(),
    entityName: z.string(),
    isUsedInActionPlan: z.boolean(),
    sourceSsqVersionId: z.number(),
    targetSsqVersionId: z.number(),
    migrate: z.number(),
    transform: z.number(),
    delete: z.number(),
    potentiallyCompatible: z.number(),
    toFill: z.number(),
    versionChanges: z.object({
        new: z.array(z.string()),
        deleted: z.record(z.number(), z.string()),
        versionChanged: z.array(
            z.object({
                code: z.string(),
                version: z.string(),
            }),
        ),
    }),
});

const CampaignSsqPostVersionComparisonSchema = z.object({
    "hydra:totalItems": z.number(),
    "hydra:member": z.array(SsqPostVersionComparisonSchema),
});

export type SsqPostVersionComparison = z.infer<typeof SsqPostVersionComparisonSchema>;
export type CampaignSsqPostVersionComparison = {
    totalItems: number;
    comparisons: SsqPostVersionComparison[];
};

export const useGetCampaignSsqPostVersionComparison = () => {
    return useMutation({
        mutationFn: async ({
            campaignId,
            ssqVersionId,
            sourceSsqVersionId,
            page,
            itemsPerPage,
        }: GetCampaignSsqPostVersionComparisonPayload) => {
            const endpoint = `/api/campaigns/${campaignId}/ssq-post-version-comparison`;
            const response = await loggedInApiCall.get(endpoint, {
                params: {
                    ssq_version_id: ssqVersionId,
                    source_ssq_version_id: sourceSsqVersionId,
                    page,
                    itemsPerPage,
                },
            });

            const data = zodParseWithDetailedLog(response.data, CampaignSsqPostVersionComparisonSchema, {
                operation: "GET",
                endpoint,
            });

            return {
                totalItems: data["hydra:totalItems"],
                comparisons: data["hydra:member"],
            };
        },
    });
};
